import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import CTASection from '../ui/CTASection';
import ContactModal from '../ui/ContactModal';
import Footer from '../layout/Footer';
import { 
  FaHome, 
  FaHammer, 
  FaTools, 
  FaUtensils, 
  FaBath, 
  FaBuilding, 
  FaPaintRoller, 
  FaPlus, 
  FaCog, 
  FaThermometerHalf, 
  FaBrush, 
  FaWindowMaximize, 
  FaTree,
  FaWrench,
  FaBolt,
  FaArrowLeft,
  FaPhone,
  FaHardHat,
  FaImages,
  FaTimes,
  FaUsers
} from 'react-icons/fa';
import './AllServices.css';

const AllServicesPage = memo(() => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedService, setSelectedService] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [serviceForContact, setServiceForContact] = useState(null); // Store service for contact modal
  const previousVisibleCountRef = useRef(0);
  const lastFocusedElementRef = useRef(null);
  const [loadedImages, setLoadedImages] = useState(new Set());
  



  const handleContactClick = useCallback((e) => {
    e.preventDefault();
    window.location.href = 'tel:+13136316020';
  }, []);

  const handleServiceClick = useCallback((service, event) => {
    // Store the element that triggered the modal
    lastFocusedElementRef.current = event?.currentTarget || document.activeElement;
    setSelectedService(service);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedService(null);
    // Don't clear serviceForContact here - we need it for the contact modal
    // Remove modal-open class from body
    document.body.classList.remove('modal-open');
    
    // Defer focus management to avoid blocking the close animation
    requestAnimationFrame(() => {
      if (lastFocusedElementRef.current) {
        lastFocusedElementRef.current.focus();
      }
    });
  }, []);

  const handleGetQuoteClick = useCallback(() => {
    // Store the selected service before closing the modal
    setServiceForContact(selectedService);
    closeModal(); // Close the service modal first
    setIsContactModalOpen(true); // Open the contact modal
  }, [selectedService, closeModal]);

  const handleCTAClick = useCallback(() => {
    setIsContactModalOpen(true);
  }, []);

  // Function to get the appropriate background image for each service
  const getServiceBackgroundImage = (serviceTitle) => {
    const serviceImageMap = {
      'Full Service Renovations': '/images/services/Full Service Renovation/service-renovation1.webp',
      'Foundations': '/images/services/Full Gut Rehab/full-gut-rehab1.webp',
      'Roofing': '/images/services/roofing/roofing1.webp',
      'Kitchens': '/images/services/kitchens/kitchens1.webp',
      'Bathrooms': '/images/services/bathrooms/bathroom1.webp',
      'Basements': '/images/services/basements/basement1.webp',
      'Siding': '/images/services/Siding/siding1.webp',
      'Additions': '/images/services/home-additions/home-additions1.webp',
      'Full Gut Rehabs': '/images/services/Full Gut Rehab/full-gut-rehab1.webp',
      'Plumbing': '/images/services/plumbing/plumbing1.webp',
      'Framing': '/images/services/Framing/framing1.webp',
      'Cement': '/images/services/home-cement/home-cement1.webp',
      'HVAC': '/images/services/HVAC/hvac1.webp',
      'Electrical': '/images/services/Electrical/electrical1.webp',
      'Painting': '/images/services/Paint/paint1.webp',
      'Drywall': '/images/services/Drywall/drywall.webp',
      'Windows & Doors': '/images/services/Windows and doors/windows-and-doors1.webp',
      'Fences and Decks': '/images/services/Fences and Decks/fences-and-decks1.webp',
      'Tile Work': '/images/services/home-tiles/home-tiles1.webp'
    };
    
    return serviceImageMap[serviceTitle] || '/images/services/roofing/roofing1.webp';
  };
  const [visibleCount, setVisibleCount] = useState(() => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth >= 992) {
        return 8; // Desktop (992px+)
      } else if (window.innerWidth >= 768 && window.innerWidth <= 991) {
        return 6; // Tablet (768px - 991px)
      } else {
        return 6; // Mobile (320px - 767px)
      }
    }
    return 6; // Default for server-side rendering (mobile-first)
  });
  
  const services = [
    {
      icon: FaHome,
      title: 'Full Service Renovations',
      description: 'Complete home transformations from foundation to finish. We handle every aspect of your renovation project.',
      features: ['Project Planning', 'Permit Acquisition', 'Quality Control', 'Timely Completion']
    },
    {
      icon: FaHome,
      title: 'Foundations',
      description: 'Expert foundation work including repairs, waterproofing, and new construction.',
      features: ['Foundation Repair', 'Waterproofing', 'New Construction', 'Structural Assessment']
    },
    {
      icon: FaHome,
      title: 'Roofing',
      description: 'Professional roofing services for all types of residential and commercial properties.',
      features: ['Roof Repair', 'Residential' , 'Commercial' , 'Gutter Systems']
    },
    {
      icon: FaUtensils,
      title: 'Kitchens',
      description: 'Complete kitchen transformations with modern designs and premium materials.',
      features: ['Custom Cabinetry', 'Countertops', 'Appliances', 'Lighting & Electrical']
    },
    {
      icon: FaBath,
      title: 'Bathrooms',
      description: 'Luxury bathroom renovations with spa-like features and elegant finishes.',
      features: ['Walk-in Showers', 'Freestanding Tubs', 'Tile Work', 'Plumbing & Fixtures']
    },
    {
      icon: FaBuilding,
      title: 'Basements',
      description: 'Transform your basement into functional living space with proper finishing.',
      features: ['Basement Finishing', 'Waterproofing', 'Egress Windows', 'Flooring & Walls']
    },
    {
      icon: FaPaintRoller,
      title: 'Siding',
      description: 'Protect and beautify your home with professional siding installation and repair.',
      features: ['Vinyl Siding', 'Fiber Cement', 'Wood Siding', 'Maintenance & Repair']
    },
    {
      icon: FaPlus,
      title: 'Additions',
      description: 'Expand your living space with seamless home additions and extensions.',
      features: ['Room Additions', 'Second Stories', 'Sunrooms', 'Garage Conversions']
    },
    {
      icon: FaHammer,
      title: 'Full Gut Rehabs',
      description: 'Complete home renovations from the studs out with modern finishes and systems.',
      features: ['Structural Work', 'New Systems', 'Modern Finishes', 'Code Compliance']
    },
    {
      icon: FaWrench,
      title: 'Plumbing',
      description: 'Professional plumbing services for all your residential and commercial needs.',
      features: ['Pipe Installation', 'Fixture Installation', 'Drain Cleaning', 'Emergency Repairs']
    },
    {
      icon: FaTools,
      title: 'Framing',
      description: 'Expert framing services for new construction, additions, and structural modifications.',
      features: ['New Construction', 'Additions', 'Structural Modifications', 'Code Compliance']
    },
    {
      icon: FaCog,
      title: 'Cement',
      description: 'Professional concrete work including driveways, patios, and foundation work.',
      features: ['Driveways', 'Patios', 'Sidewalks', 'Foundation Work']
    },
    {
      icon: FaThermometerHalf,
      title: 'HVAC',
      description: 'Complete heating, ventilation, and air conditioning services for year-round comfort.',
      features: ['System Installation', 'Maintenance', 'Repairs', 'Energy Efficiency']
    },
    {
      icon: FaBolt,
      title: 'Electrical',
      description: 'Licensed electrical services for all your residential and commercial needs.',
      features: ['Wiring', 'Panel Upgrades', 'Lighting', 'Safety Inspections']
    },
    {
      icon: FaBrush,
      title: 'Painting',
      description: 'Professional interior and exterior painting services with quality materials.',
      features: ['Interior Painting', 'Exterior Painting', 'Color Consultation', 'Surface Prep']
    },
    {
      icon: FaHome,
      title: 'Drywall',
      description: 'Expert drywall installation, repair, and finishing for smooth, professional walls.',
      features: ['Installation', 'Repair', 'Taping & Mudding', 'Texture Matching']
    },
    {
      icon: FaWindowMaximize,
      title: 'Windows & Doors',
      description: 'Energy-efficient windows and doors to improve comfort and curb appeal.',
      features: ['Window Installation', 'Door Installation', 'Energy Efficiency', 'Security Features']
    },
    {
      icon: FaTree,
      title: 'Fences and Decks',
      description: 'Beautiful outdoor structures to enhance your property value and enjoyment.',
      features: ['Deck Construction', 'Fence Installation', 'Maintenance', 'Custom Design']
    },
    {
      icon: FaBrush,
      title: 'Tile Work',
      description: 'Professional tile installation and repair for bathrooms, kitchens, and flooring.',
      features: ['Bathroom Tiles', 'Kitchen Backsplash', 'Floor Tiles', 'Tile Repair & Maintenance']
    }
  ];

  // Filter services based on search term
  const filteredServices = services.filter(service =>
    service.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    service.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    service.features.some(feature => 
      feature.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  // Determine pagination size based on screen size
  const getPaginationSize = () => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth >= 992) {
        return 8; // Desktop (992px+)
      } else if (window.innerWidth >= 768 && window.innerWidth <= 991) {
        return 6; // Tablet (768px - 991px)
      } else {
        return 6; // Mobile (320px - 767px)
      }
    }
    return 6; // Default for server-side rendering (mobile-first)
  };

  // Get visible services based on pagination
  const visibleServices = filteredServices.slice(0, visibleCount);
  const hasMoreServices = filteredServices.length > visibleCount;


  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setVisibleCount(getPaginationSize()); // Reset to appropriate pagination size when searching
  };

  const handleShowMore = (e) => {
    // Prevent event bubbling and default behavior to avoid touch conflicts
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    const paginationSize = getPaginationSize();
    const previousCount = visibleCount;
    const newCount = Math.min(visibleCount + paginationSize, filteredServices.length);
    
    // Store the previous count for focus management
    previousVisibleCountRef.current = previousCount;
    
    setVisibleCount(newCount);
    
    // Focus on the first new service card after DOM update
    setTimeout(() => {
      const firstNewServiceIndex = previousCount;
      const serviceCards = document.querySelectorAll('.service-card');
      const firstNewServiceCard = serviceCards[firstNewServiceIndex];
      
      if (firstNewServiceCard) {
        // Find the focusable element (button or link)
        const focusableElement = firstNewServiceCard.querySelector('button, a, [href], [tabindex="0"]');
        if (focusableElement) {
          focusableElement.focus({ preventScroll: true });
          // Scroll the element into view smoothly
          firstNewServiceCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          // If no focusable element, make the card itself focusable
          firstNewServiceCard.setAttribute('tabindex', '-1');
          firstNewServiceCard.focus({ preventScroll: true });
          firstNewServiceCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }, 150);
  };

  // Handle window resize to adjust pagination (with debouncing to prevent iPhone touch conflicts)
  useEffect(() => {
    let resizeTimeout;
    
    const handleResize = () => {
      // Clear any existing timeout
      clearTimeout(resizeTimeout);
      
      // Debounce resize events to prevent iPhone touch conflicts
      resizeTimeout = setTimeout(() => {
        const paginationSize = getPaginationSize();
        setVisibleCount(prev => {
          // Only reset if we're showing fewer services than the new pagination size
          // This prevents resetting when users have already loaded more services
          if (prev <= paginationSize) {
            return Math.min(paginationSize, filteredServices.length);
          }
          // Keep the current count if user has already loaded more services
          return prev;
        });
      }, 150); // 150ms debounce to prevent touch conflicts
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
    };
  }, [filteredServices.length]);


  // Focus management when new services are shown
  useEffect(() => {
    if (previousVisibleCountRef.current > 0 && visibleCount > previousVisibleCountRef.current) {
      // New services were added, focus on the first new one
      setTimeout(() => {
        const firstNewServiceIndex = previousVisibleCountRef.current;
        const serviceCards = document.querySelectorAll('[role="button"][tabindex="0"]');
        const firstNewServiceCard = serviceCards[firstNewServiceIndex];
        if (firstNewServiceCard) {
          firstNewServiceCard.focus();
        }
      }, 100);
    }
  }, [visibleCount]);

  // Lazy load images using Intersection Observer
  useEffect(() => {
    const imageObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cardElement = entry.target;
            const imageUrl = cardElement.getAttribute('data-bg-image');
            if (imageUrl && !loadedImages.has(imageUrl)) {
              // Preload image
              const img = new Image();
              img.onload = () => {
                cardElement.style.backgroundImage = `url('${imageUrl}')`;
                setLoadedImages(prev => new Set(prev).add(imageUrl));
              };
              img.src = imageUrl;
              imageObserver.unobserve(cardElement);
            }
          }
        });
      },
      { rootMargin: '50px' }
    );

    const serviceCards = document.querySelectorAll('.service-card[data-bg-image]');
    serviceCards.forEach(card => imageObserver.observe(card));

    return () => imageObserver.disconnect();
  }, [visibleServices, loadedImages]);

  // Focus trapping and body scroll locking for modal
  useEffect(() => {
    if (isModalOpen) {
      // Lock body scroll
      document.body.classList.add('modal-open');
      
      // Focus the close button when modal opens
      const closeButton = document.querySelector('.modal-close');
      if (closeButton) {
        closeButton.focus();
      }

      // Focus trapping
      const modalContent = document.querySelector('.modal-content');
      const focusableElements = modalContent?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      
      const firstFocusableElement = focusableElements?.[0];
      const lastFocusableElement = focusableElements?.[focusableElements.length - 1];

      const handleTabKey = (e) => {
        if (e.key === 'Tab') {
          if (e.shiftKey) {
            // Shift + Tab
            if (document.activeElement === firstFocusableElement) {
              lastFocusableElement?.focus();
              e.preventDefault();
            }
          } else {
            // Tab
            if (document.activeElement === lastFocusableElement) {
              firstFocusableElement?.focus();
              e.preventDefault();
            }
          }
        }
      };

      const handleEscapeKey = (e) => {
        if (e.key === 'Escape') {
          closeModal();
        }
      };

      document.addEventListener('keydown', handleTabKey);
      document.addEventListener('keydown', handleEscapeKey);

      return () => {
        document.removeEventListener('keydown', handleTabKey);
        document.removeEventListener('keydown', handleEscapeKey);
        document.body.classList.remove('modal-open');
      };
    }
  }, [isModalOpen, closeModal]);


  return (
    <main className="all-services-page">
      <Helmet>
        <title>RC Remodel - All Services | Kitchen, Bathroom & Home Renovation</title>
        <meta name="description" content="Comprehensive home remodeling services including kitchens, bathrooms, foundations, roofing, plumbing, electrical, HVAC, and more. Licensed contractors with 10+ years experience." />
        <meta name="keywords" content="remodeling services, kitchen renovation, bathroom remodel, roofing, plumbing, electrical, HVAC, home renovation, construction services" />
        <link rel="canonical" href="https://rcremodel.com/all-services" />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://rcremodel.com/all-services" />
        <meta property="og:title" content="RC Remodel - All Services | Kitchen, Bathroom & Home Renovation" />
        <meta property="og:description" content="Comprehensive home remodeling services including kitchens, bathrooms, foundations, roofing, plumbing, electrical, HVAC, and more." />
        <meta property="og:image" content="https://rcremodel.com/images/services/kitchens/kitchens1.webp" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="RC Remodel - All Services | Kitchen, Bathroom & Home Renovation" />
        <meta name="twitter:description" content="Comprehensive home remodeling services. Licensed contractors with 10+ years experience." />
        <meta name="twitter:image" content="https://rcremodel.com/images/services/kitchens/kitchens1.webp" />
      </Helmet>
      <div className="page-header">
        <div className="container">
          <div className="page-nav">
            <Link to="/" className="back-btn">
              <FaArrowLeft className="back-icon" />
              Back to Home
            </Link>
            <div className="page-nav-right">
              <button 
                className="nav-action-btn" 
                onClick={handleContactClick} 
                title="Call us"
              >
                <FaPhone className="nav-icon" />
                <span className="nav-text">Call us</span>
              </button>
              <button 
                className="nav-action-btn active" 
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                title="Our Services"
              >
                <FaHardHat className="nav-icon" />
                <span className="nav-text">Our Services</span>
              </button>
              <Link 
                to="/gc-services" 
                className="nav-action-btn" 
                title="General Contractor"
              >
                <FaUsers className="nav-icon" />
                <span className="nav-text">General Contractor</span>
              </Link>
              <Link 
                to="/gallery" 
                className="nav-action-btn" 
                title="Our Gallery"
              >
                <FaImages className="nav-icon" />
                <span className="nav-text">Our Gallery</span>
              </Link>
            </div>
          </div>
          <h1 className="page-title">Our Services</h1>
          <p className="page-subtitle">
            Comprehensive home remodeling services to transform your space into your dream home.
          </p>
        </div>
      </div>

      <div className="all-services-content">
        <div className="container">
          {/* Search Bar */}
          <div className="search-section">
          <div className="search-container">
            <div className="search-input-wrapper">
              <input 
                type="text" 
                placeholder="Search services..." 
                value={searchTerm} 
                onChange={handleSearchChange}
                className="search-input"
              />
              <div className="search-icon">
                <svg 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                  aria-label="Search icon"
                  role="img"
                >
                  <path d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
            {searchTerm && (
              <div className="search-results-info">
                {filteredServices.length} service{filteredServices.length !== 1 ? 's' : ''} found
              </div>
            )}
          </div>
        </div>

        {/* Services Grid */}
        {visibleServices.length > 0 ? (
          <div className="services-grid pad-h-10 pad-h-sm-6 pad-h-xs-0">
            {visibleServices.map((service, index) => (
              <div 
                key={index} 
                className="service-card"
                data-bg-image={getServiceBackgroundImage(service.title)}
                style={{
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  backgroundColor: '#f0f0f0'
                }}
                onClick={(e) => handleServiceClick(service, e)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleServiceClick(service, e);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={`View details for ${service.title}`}
              >
                <div className="service-overlay"></div>
                <h2 className="service-title">{service.title}</h2>
              </div>
            ))}
          </div>
        ) : null}

        {/* Show More Button */}
        {visibleServices.length > 0 && hasMoreServices && (
          <div className="show-more-section">
            <button 
              className="show-more-btn" 
              onClick={(e) => handleShowMore(e)}
              onTouchStart={(e) => {
                // Prevent touch conflicts on iPhone
                // Note: preventDefault() removed due to passive event listener
              }}
              onTouchEnd={(e) => {
                // Handle touch end to prevent conflicts
                // Note: preventDefault() removed due to passive event listener
                handleShowMore(e);
              }}
              tabIndex={0}
            >
              Show More Services
              <span className="show-more-count">
                ({filteredServices.length - visibleCount} remaining)
              </span>
            </button>
          </div>
        )}

        {searchTerm && visibleServices.length === 0 ? (
          <div className="no-results">
            <div className="no-results-icon">
              <svg 
                width="64" 
                height="64" 
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
                aria-label="No search results found"
                role="img"
              >
                <path d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>No services found</h3>
            <p>Try searching for a different term or browse all our services.</p>
            <button 
              className="clear-search-btn"
              onClick={() => setSearchTerm('')}
              tabIndex={0}
            >
              Clear Search
            </button>
          </div>
        ) : null}

          <CTASection 
            showModal={false}
            onClick={handleCTAClick}
          />
        </div>
      </div>
      
      {/* Service Details Modal */}
      {isModalOpen && selectedService && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <button 
                className="modal-close" 
                onClick={closeModal}
                tabIndex={0}
                aria-label="Close modal"
              >
              </button>
            </div>
            
            <div className="modal-body">
              <div 
                className="modal-image-section"
                style={{
                  backgroundImage: `url('${getServiceBackgroundImage(selectedService.title)}')`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat'
                }}
              >
                <div className="modal-image-overlay"></div>
                <h2 className="modal-title-overlay">{selectedService.title}</h2>
                <p className="modal-description">{selectedService.description}</p>
              </div>
              
              <div className="modal-title-section">
              </div>
              
              <div className="modal-features">
                <h3 className="features-title margin-b-xs-0">
                  What We Offer
                </h3>
                <div className="features-grid">
                  {selectedService.features.map((feature, index) => (
                    <div key={index} className="feature-item">
                      <div className="feature-check">
                        <span className="check-icon">✓</span>
                      </div>
                      <span className="feature-text">{feature}</span>
                    </div>
                  ))}
                </div>
                
                {/* 100% Financing Badge */}
                <div className="all-services-financing-badge margin-b-8 margin-b-xs-4">
                  <div className="badge-content">
                    <span className="badge-title">100% FINANCING AVAILABLE</span>
                    <span className="badge-subtitle">No upfront costs • Flexible payment options</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <div className="modal-actions">
                <button 
                  className="modal-btn modal-btn-primary" 
                  onClick={handleGetQuoteClick}
                  tabIndex={0}
                >
                  Message Us For More Details
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Contact Modal */}
      <ContactModal 
        isOpen={isContactModalOpen} 
        onClose={useCallback(() => {
          setIsContactModalOpen(false);
          setServiceForContact(null); // Clear the stored service when contact modal closes
        }, [])}
        selectedService={serviceForContact}
      />
      
      <Footer />
    </main>
  );
});

AllServicesPage.displayName = 'AllServicesPage';

export default AllServicesPage;
